import { useIdentityStorage } from '../adapters/ports.js';

/**
 * Where the signed-in session lives on the device: the platform token and the
 * refresh token that renews it, as the identity server issued them.
 *
 * Both are bound to this browser's proof key (see proofKey.js), so a copy of
 * either is worthless anywhere else — which is what makes the storage seam an
 * acceptable home for them: mobile answers with Capacitor Preferences, a
 * desktop app with `localStorage`, a test with a Map.
 *
 * Bump this suffix when the persisted shape changes so a stale session is
 * discarded rather than blowing up the auth flow on the next boot.
 */
const SESSION_KEY = 'aac.session.v2';

// The "never reject" rule lives in the storage driver. It matters most here:
// getSession() runs on the boot path, before the app mounts, so a rejection
// aborts initialization and leaves a blank screen.
export async function getSession() {
    const raw = await useIdentityStorage().get(SESSION_KEY);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/** Stores the token endpoint's answer, with its lifetimes turned into wall-clock times. */
export async function setSession(tokens) {
    const now = Date.now();
    const session = {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: now + tokens.expires_in * 1000,
        refreshExpiresAt: now + tokens.refresh_expires_in * 1000
    };
    await useIdentityStorage().set(SESSION_KEY, JSON.stringify(session));
    return session;
}

export async function clearSession() {
    await useIdentityStorage().remove(SESSION_KEY);
}

export async function hasSession() {
    return (await getSession()) !== null;
}

export const sessionTokenStorage = { getSession, setSession, clearSession, hasSession };
export default sessionTokenStorage;
